'use client'

import { useAuth0 } from '@auth0/auth0-react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useEffect } from 'react'
import { useUserStore } from '@/stores/user-store'

export const DASHBOARD_USER_URLS = ['/dashboard']
export const MARKETING_URLS = ['/reliability-check']
export const ADMIN_ONLY_URLS = ['/backoffice', '/raw']

// Doku: https://auth0.com/docs/quickstart/spa/react/01-login
export default function CheckAuthorization({ children }: { children: React.ReactNode }) {
  const { loginWithRedirect } = useAuth0()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const router = useRouter()

  const user = useUserStore((state) => state.user)
  const isAdmin = useUserStore((state) => state.isAdmin)
  const isDashboardUser = useUserStore((state) => state.isDashboardUser)
  const userRoles = useUserStore((state) => state.userRoles)
  const isMarketingUser = userRoles.some((role) => role.startsWith('marketing-'))
  const storeError = useUserStore((state) => state.error)
  const userStateIsLoading = useUserStore((state) => state.loading)

  const isAuthenticated = !!user

  const urlIsDashboardUserOnly = DASHBOARD_USER_URLS.some((allowedUrl) => pathname.startsWith(allowedUrl))
  const urlIsAdminOnly = ADMIN_ONLY_URLS.some((allowedUrl) => pathname.startsWith(allowedUrl))
  const urlIsMarketingOnly = MARKETING_URLS.some((allowedUrl) => pathname.startsWith(allowedUrl))
  const urlNeedsLogin = urlIsDashboardUserOnly || urlIsAdminOnly || urlIsMarketingOnly

  // TODO: when error message: "JWT token expired" -> do something!
  // E.g. show proper message and button to trigger loginWithRedirect() again

  const redirectToDashboard = useCallback(() => {
    if (pathname === '/' || !urlIsDashboardUserOnly) {
      router.push('/dashboard')
    }
  }, [pathname, router, urlIsDashboardUserOnly])

  const redirectToMarketing = useCallback(() => {
    console.warn('Redirecting to marketing page... from ', pathname)
    if (pathname === '/' || !urlIsMarketingOnly) {
      router.push('/reliability-check')
    }
  }, [pathname, router, urlIsMarketingOnly])

  useEffect(() => {
    if (userStateIsLoading) {
      return
    }

    if (pathname !== '/' && !urlNeedsLogin) {
      console.log('URL is public, no need to check authentication:', pathname)
      return
    }

    if (urlNeedsLogin) {
      console.log(
        'URL needs login, checking authentication...',
        pathname,
        urlIsDashboardUserOnly ? 'urlIsDashboardUserOnly' : 'urlIs NOT DashboardUserOnly',
      )
      if (!isAuthenticated) {
        loginWithRedirect()
        return
      }

      if (!isAdmin && !isDashboardUser && !isMarketingUser) {
        router.push('/')
        return
      }
    }

    if (urlIsAdminOnly && !isAdmin) {
      console.warn('*** urlIsAdminOnly', pathname)
      if (isDashboardUser && !isMarketingUser) redirectToDashboard()
      else if (isMarketingUser) redirectToMarketing()
      else router.push('/')
      return
    }

    if (urlIsMarketingOnly && !(isMarketingUser || isAdmin)) {
      router.push('/')
      return
    }

    if (isMarketingUser && !urlIsMarketingOnly) {
      console.warn('*** isMarketingUser && !urlIsMarketingOnly', pathname)
      redirectToMarketing()
      return
    }

    if (pathname === '/' && (isDashboardUser || isAdmin) && !isMarketingUser) {
      redirectToDashboard()
      return
    }
  }, [
    pathname,
    searchParams,
    isAuthenticated,
    isAdmin,
    isDashboardUser,
    isMarketingUser,
    loginWithRedirect,
    redirectToDashboard,
    redirectToMarketing,
    router,
    urlIsAdminOnly,
    urlIsDashboardUserOnly,
    urlIsMarketingOnly,
    urlNeedsLogin,
    userStateIsLoading,
    user,
  ])

  if (storeError) return <div>An unexpected error occurred: {storeError?.message}</div>

  if (userStateIsLoading) return null

  return children
}
